/**
 * Centralized faker configuration for consistent mock data generation.
 *
 * Usage:
 *   import { faker } from 'shared/utils/faker';
 *
 * This pre-configured instance uses Dutch locale and can be extended
 * with custom generators for domain-specific data.
 */

import { Faker, nl, en } from "@faker-js/faker";

// --- Configuration ---

const FAKER_CONFIG = {
  /**
   * Primary locale for generated data.
   * Options: 'nl' (Dutch), 'en' (English), etc.
   * See: https://fakerjs.dev/guide/localization
   */
  locale: [nl, en], // Dutch primary, English fallback

  /**
   * Optional seed for reproducible data during development.
   * Set to a number for consistent data across page reloads, or undefined for random.
   */
  seed: undefined as number | undefined,
};

// --- Create configured faker instance ---

const baseFaker = new Faker({ locale: FAKER_CONFIG.locale });

if (FAKER_CONFIG.seed !== undefined) {
  baseFaker.seed(FAKER_CONFIG.seed);
}

// --- Custom helpers for Dutch school context ---

const MENTOR_CLASSES = [
  "1A",
  "1B",
  "1C",
  "1D",
  "2A",
  "2B",
  "2C",
  "2D",
  "3A",
  "3B",
  "3C",
  "3D",
  "4A",
  "4B",
  "4C",
  "4D",
  "5A",
  "5B",
  "5C",
  "6A",
  "6B",
  "6C",
];

const DUTCH_MIDDLE_NAMES = ["van", "van de", "van den", "de", "van der", "ter"];

const SUBJECTS = [
  "Nederlands",
  "Engels",
  "Wiskunde",
  "Natuurkunde",
  "Scheikunde",
  "Biologie",
  "Geschiedenis",
  "Aardrijkskunde",
  "Economie",
  "Maatschappijleer",
  "Informatica",
  "Kunst",
  "Muziek",
  "Lichamelijke opvoeding",
];

// --- Extended faker with school-specific generators ---

export const faker = Object.assign(baseFaker, {
  school: {
    mentorClass: () => baseFaker.helpers.arrayElement(MENTOR_CLASSES),
    mentorClasses: (count: number) =>
      baseFaker.helpers.arrayElements(MENTOR_CLASSES, count),
    subject: () => baseFaker.helpers.arrayElement(SUBJECTS),
    subjects: (count: number) =>
      baseFaker.helpers.arrayElements(SUBJECTS, count),
    dutchMiddleName: () =>
      baseFaker.datatype.boolean({ probability: 0.3 })
        ? baseFaker.helpers.arrayElement(DUTCH_MIDDLE_NAMES)
        : "",
    studentFullName: () => {
      const firstName = baseFaker.person.firstName();
      const middleName = baseFaker.datatype.boolean({ probability: 0.3 })
        ? baseFaker.helpers.arrayElement(DUTCH_MIDDLE_NAMES)
        : "";
      const lastName = baseFaker.person.lastName();

      return middleName
        ? `${firstName} ${middleName} ${lastName}`
        : `${firstName} ${lastName}`;
    },
  },
});

export default faker;
